<template>
  <div class="selfie-container totemBG">
    <!-- Header com Logo, Botão Voltar e Botões de Orientação -->
    <div class="header">
      <!-- Área superior com logo e reinício -->
      <div class="header-top">
        <div class="selfie-brand">
          <vLogoDefault />
        </div>
        <div class="tw-text-center">
          <vBtnReset />
        </div>
      </div>

      <div class="selfie-navigation">
        <button @click="voltar" class="btn-voltar">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="tw-w-8 tw-h-8"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          Voltar
        </button>
        <TotemAssistanceActions inline />
      </div>

      <div class="orientation-buttons">
        <button
          @click="setPortraitMode"
          class="orientation-btn portrait-btn tw-uppercase"
          :class="{ active: isPortraitMode }"
        >
          Foto em pé
        </button>

        <button
          @click="setLandscapeMode"
          class="orientation-btn landscape-btn tw-uppercase"
          :class="{ active: !isPortraitMode }"
        >
          Foto deitada
        </button>
      </div>
    </div>

    <div class="main-content">
      <div class="camera-container">
        <!-- ph-no-capture: bloqueia o elemento na gravação de sessão do
             PostHog. Sem isto o replay levaria junto o rosto do cliente. -->
        <video
          ref="video"
          class="camera-video ph-no-capture tw-mx-auto tw-shadow-lg tw-shadow-gray-300 tw-rounded"
          autoplay
          muted
          playsinline
        ></video>

        <div
          v-if="
            !isCountingDown &&
            !showLookAtCameraMessage &&
            photosTaken < maxPhotos
          "
          class="instructions"
        >
          <div class="instruction-text">TOQUE NA TELA PARA CAPTURAR</div>
        </div>
        <!-- :style="`width: ${getCamSize.width}px; height: ${getCamSize.height}px;`" -->

        <!-- Overlay da Câmera -->
        <div class="camera-overlay" @click="handleScreenTap">
          <!-- Flash de Captura -->
          <div v-if="showFlash" class="capture-flash"></div>
          <!-- Mensagem de Olhar para a Câmera -->
          <div v-if="showLookAtCameraMessage" class="look-camera-message">
            <div class="message-content">
              <div class="message-text">Olhe para a câmera</div>
            </div>
          </div>
          <!-- Contagem Regressiva -->
          <div v-if="isCountingDown" class="countdown-overlay">
            <div class="countdown-center">
              <div class="countdown-clock-bg"></div>
              <span class="countdown-number">{{ countdownNumber }}</span>
            </div>
          </div>

          <!-- Indicador de Progresso -->
          <div class="progress-indicator">
            <div class="progress-text">
              {{ photosTaken }}/{{ maxPhotos }} fotos
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: `${(photosTaken / maxPhotos) * 100}%` }"
              ></div>
            </div>
          </div>

          <!-- Instruções -->
        </div>

        <!-- Botão de Captura -->
        <button
          v-if="
            !isCountingDown &&
            !showLookAtCameraMessage &&
            photosTaken < maxPhotos
          "
          @click="iniciarCaptura"
          class="capture-button"
          :disabled="!cameraReady"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-12 h-12"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
            />
          </svg>
        </button>

        <!-- Botão de Galeria -->
        <button
          @click="abrirGaleria"
          class="gallery-button"
          :class="{ 'has-photos': photosTaken > 0 }"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-8 h-8"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
            />
          </svg>
          <span v-if="photosTaken > 0" class="photo-count">{{
            photosTaken
          }}</span>
        </button>
      </div>

      <!-- Mini Galeria -->
      <div v-if="photosTaken > 0" class="mini-gallery ph-no-capture">
        <div
          v-for="(photo, index) in gallery"
          :key="index"
          class="mini-photo"
          :class="{ 'has-photo': photo }"
        >
          <img
            draggable="false"
            @contextmenu.prevent
            @dragstart.prevent
            v-if="photo"
            :src="photo"
            :alt="`Foto ${index + 1}`"
          />
          <div v-else class="mini-placeholder">{{ index + 1 }}</div>
        </div>
      </div>
    </div>

    <!-- Modal da Galeria -->
    <vGaleria
      v-if="showGallery"
      :gallery="gallery"
      @closeGallery="fecharGaleria"
      @cancelGallery="cancelarGaleria"
      @backToSelfie="voltarParaSelfie"
      @deletePhoto="deletarFoto"
      @confirmPhotos="confirmarFotos"
    />

    <!-- Loading de Upload -->
    <div v-if="isUploading" class="upload-overlay">
      <div class="upload-content">
        <div class="upload-spinner"></div>
        <div class="upload-text">{{ uploadStatus || "Salvando fotos..." }}</div>
        <div class="upload-progress">
          <div class="progress-bar">
            <div
              class="progress-fill"
              :style="{ width: `${uploadProgress}%` }"
            ></div>
          </div>
          <div class="progress-percentage">
            {{ Math.round(uploadProgress) }}%
          </div>
        </div>
      </div>
    </div>

    <!-- Pop-up entre fotos -->
    <div v-if="showNextPhotoPopup" class="next-photo-popup">
      <div class="popup-content">
        <div class="popup-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-16 h-16"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z"
            />
          </svg>
        </div>
        <div class="popup-text">
          <h3>Foto Capturada!</h3>
          <p>Click para próxima foto!</p>
        </div>
        <button @click="fecharPopupProximaFoto" class="popup-button">
          Continuar
        </button>
      </div>
    </div>

    <!-- Pop-up de lembrete para finalizar -->
    <div v-if="showFinishReminderPopup" class="finish-reminder-popup">
      <div class="popup-content">
        <div class="popup-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-16 h-16"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>
        <div class="popup-text">
          <h3>Todas as fotos foram tiradas!</h3>
          <p>Você precisa finalizar o processo na galeria para continuar</p>
        </div>
        <div class="popup-buttons">
          <button @click="abrirGaleria" class="popup-button primary">
            Abrir Galeria
          </button>
          <button @click="fecharPopupLembrete" class="popup-button secondary">
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Componentes do Sistema -->
    <vTimeout :segundos="$root.getTimeoutName('SELFIE')" />
  </div>
</template>

<script>
import vGaleria from "./vGaleria.vue";
import vBtnReset from "@/components/vBtnReset.vue";
import vTimeout from "@/components/vTimeount.vue";
import TotemAssistanceActions from "@/components/assistance/TotemAssistanceActions.vue";
import etapa from "@/constants/etapaCompra";
import { setEtapaCompra, uploadImageStorage, db } from "@/service/firebase";
import { addDoc, collection } from "firebase/firestore";
import posthog from "@/posthog";

// AudioContext único para toda a vida do app. Criar um novo a cada captura
// estoura o limite (~6) do Chrome, passa a lançar exceção e trava o totem
// (só recuperável reiniciando o Windows). Reaproveitar um único contexto evita
// o vazamento.
let sharedAudioContext = null;

export default {
  name: "vSelfie",
  components: {
    vGaleria,
    vBtnReset,
    vTimeout,
    TotemAssistanceActions,
  },
  data() {
    return {
      video: null,
      stream: null,
      cameraReady: false,
      isCountingDown: false,
      showLookAtCameraMessage: false,
      countdownNumber: 5,
      countdownInterval: null,
      photosTaken: 0,
      gallery: [],
      showGallery: false,
      isUploading: false,
      maxPhotos: 0,
      showFlash: false,
      showNextPhotoPopup: false,
      showFinishReminderPopup: false,
      isPortraitMode: true,
      uploadProgress: 0,
      uploadStatus: "",
      destruido: false,
      trocandoOrientacao: false,
      timers: [],
      camSize: {
        width: 720,
        height: 1280,
      },
    };
  },
  computed: {
    getDefaultCamSize() {
      return {
        width: 720,
        height: 1280,
      };
    },
    getCamSize() {
      return {
        width: this.camSize.width,
        height: this.camSize.height,
      };
    },
  },
  async mounted() {
    await this.initialize();
  },
  beforeDestroy() {
    this.destruido = true;
    this.cleanupCamera();
  },
  methods: {
    // Agenda um timer rastreado. Os setTimeout soltos que existiam aqui
    // sobreviviam ao componente: ao sair da tela no meio da contagem, eles
    // ainda disparavam `iniciarContador()`, criando um setInterval órfão que
    // rodava indefinidamente fora da tela.
    agendar(callback, ms) {
      const id = setTimeout(() => {
        this.timers = this.timers.filter((t) => t !== id);
        if (this.destruido) return;
        callback();
      }, ms);
      this.timers.push(id);
      return id;
    },
    async initialize() {
      try {
        // Obter quantidade de fotos da compra
        const compra = this.$store.getters["compra/getCompra"];
        this.maxPhotos = compra.qtde_foto || 4;

        // Inicializar galeria com slots vazios
        this.gallery = new Array(this.maxPhotos).fill(null);

        // Inicializar câmera
        await this.initializeCamera();
      } catch (error) {
        this.$root.goto("/cabine/home");
      }
    },

    async initializeCamera() {
      try {
        // Verificar se o navegador suporta getUserMedia
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error("Navegador não suporta acesso à câmera");
        }

        const constraints = {
          video: {
            //facingMode: "user", // Câmera frontal
            facingMode: "user",
            width: { ideal: this.getCamSize.width },
            height: { ideal: this.getCamSize.height },
            //aspectRatio: { ideal: 16 / 9 },
            zoom: false,
            advanced: [
              { zoom: false },
              { torch: false },
              { focusMode: "continuous" },
            ],
          },
        };

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.video = this.$refs.video;

        if (this.video) {
          this.video.srcObject = this.stream;
          this.video.onloadedmetadata = () => {
            console.log(
              "Resolução real:",
              this.video.videoWidth,
              "x",
              this.video.videoHeight
            );
            this.cameraReady = true;
            // Aplicar orientação retrato por padrão
            this.applyVideoOrientation();
          };

          // Adicionar tratamento de erro para o vídeo
          this.video.onerror = () => {
            this.cameraReady = false;
          };
        }
      } catch (error) {
        // Fallback para câmera traseira se frontal não estiver disponível
        try {
          const constraints = {
            video: {
              //facingMode: "environment",
              width: { ideal: this.getCamSize.width },
              height: { ideal: this.getCamSize.height },
              //aspectRatio: { ideal: 16 / 9 },
            },
          };
          this.stream = await navigator.mediaDevices.getUserMedia(constraints);
          this.video = this.$refs.video;
          if (this.video) {
            this.video.srcObject = this.stream;
            this.video.onloadedmetadata = () => {
              this.cameraReady = true;
              // Aplicar orientação horizontal por padrão
              this.applyVideoOrientation();
            };
          }
        } catch (fallbackError) {
          // Tentar com configurações mais básicas
          try {
            const basicConstraints = {
              video: true,
            };
            this.stream = await navigator.mediaDevices.getUserMedia(
              basicConstraints
            );
            this.video = this.$refs.video;
            if (this.video) {
              this.video.srcObject = this.stream;
              this.video.onloadedmetadata = () => {
                this.cameraReady = true;
                // Aplicar orientação horizontal por padrão
                this.applyVideoOrientation();
              };
            }
          } catch (basicError) {
            this.$root.infoModal(
              "Não foi possível acessar a câmera. Verifique as permissões do navegador."
            );
          }
        }
      }
    },

    handleScreenTap() {
      if (!this.isCountingDown && this.photosTaken < this.maxPhotos) {
        this.iniciarCaptura();
      }
    },

    iniciarCaptura() {
      if (
        !this.cameraReady ||
        this.isCountingDown ||
        this.photosTaken >= this.maxPhotos ||
        this.showLookAtCameraMessage
      ) {
        return;
      }

      // Mostrar mensagem "olhe para a câmera" por 2 segundos
      this.showLookAtCameraMessage = true;

      this.agendar(() => {
        this.showLookAtCameraMessage = false;
        this.iniciarContador();
      }, 2000);
    },

    iniciarContador() {
      this.isCountingDown = true;
      this.countdownNumber = 5;

      this.countdownInterval = setInterval(() => {
        this.countdownNumber--;

        if (this.countdownNumber <= 0) {
          clearInterval(this.countdownInterval);
          this.capturarFoto();
        }
      }, 1000);
    },

    async capturarFoto(attempt = 1) {
      const MAX_TENTATIVAS = 3;
      try {
        if (!this.video || !this.video.videoWidth || !this.video.videoHeight) {
          throw new Error("Vídeo não está pronto para captura");
        }

        // Criar canvas para capturar a foto
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        // Definir dimensões do canvas
        canvas.width = this.video.videoWidth;
        canvas.height = this.video.videoHeight;

        context.drawImage(this.video, 0, 0);

        // Converter para base64 com qualidade otimizada
        let photoData = canvas.toDataURL("image/jpeg", 1);

        // Verificar se a foto foi gerada corretamente
        if (!photoData || photoData === "data:,") {
          throw new Error("Falha ao gerar imagem");
        }

        // Otimizar tamanho da foto se necessário
        //photoData = await this.optimizePhotoSize(photoData);

        // Adicionar à galeria
        this.adicionarFoto(photoData);

        // Feedback visual de captura
        this.showCaptureFeedback();

        this.isCountingDown = false;
      } catch (error) {
        this.isCountingDown = false;

        // Tentar novamente após um pequeno delay, com limite de tentativas.
        // Sem esse limite, uma falha persistente (ex.: vídeo não pronto) gera
        // um loop infinito que trava o totem.
        if (attempt < MAX_TENTATIVAS) {
          this.agendar(() => {
            if (!this.isCountingDown) {
              this.capturarFoto(attempt + 1);
            }
          }, 500);
        } else {
          // eslint-disable-next-line no-console
          console.error(
            `Falha ao capturar foto após ${MAX_TENTATIVAS} tentativas:`,
            error
          );
          this.$root.infoModal(
            "Não foi possível capturar a foto. Tente novamente."
          );
        }
      }
    },

    adicionarFoto(photoData) {
      // Encontrar o primeiro slot vazio
      const emptyIndex = this.gallery.findIndex((photo) => photo === null);

      if (emptyIndex !== -1) {
        this.gallery[emptyIndex] = photoData;
        this.photosTaken++;
        posthog.capture("selfie_photo_captured", {
          photos_captured: this.photosTaken,
          photo_target: this.maxPhotos,
        });

        // Verificar se todas as fotos foram tiradas
        if (this.photosTaken >= this.maxPhotos) {
          this.agendar(() => {
            this.abrirGaleria();
          }, 1000);
        } else {
          // Mostrar pop-up para próxima foto (exceto na última)
          this.showNextPhotoPopup = true;
        }
      }
    },

    deletarFoto(index) {
      if (this.gallery[index] !== null) {
        this.$set(this.gallery, index, null);
        this.photosTaken = Math.max(0, this.photosTaken - 1);
      }
    },

    abrirGaleria() {
      this.showGallery = true;
      this.showFinishReminderPopup = false; // Fechar pop-up de lembrete
    },

    fecharGaleria() {
      this.showGallery = false;

      // Se todas as fotos foram tiradas, mostrar pop-up de lembrete
      if (this.photosTaken >= this.maxPhotos) {
        this.agendar(() => {
          this.showFinishReminderPopup = true;
        }, 500);
      }
    },

    cancelarGaleria() {
      this.showGallery = false;

      // Limpar câmera
      this.cleanupCamera();

      // A transição pertence à ação, não à montagem da tela de destino.
      setEtapaCompra(etapa.MODO_CAPTURA).catch((error) => {
        console.error("Falha ao voltar à escolha da forma de envio:", error);
      });
    },

    voltarParaSelfie() {
      this.showGallery = false;

      // Não limpar a câmera, apenas fechar a galeria
      // Iniciar automaticamente a captura da próxima foto
      if (this.cameraReady && this.photosTaken < this.maxPhotos) {
        // Pequeno delay para garantir que a galeria fechou completamente
        this.agendar(() => {
          this.iniciarCaptura();
        }, 300);
      }
    },

    fecharPopupProximaFoto() {
      this.showNextPhotoPopup = false;

      // Iniciar automaticamente o temporizador para a próxima foto
      if (this.photosTaken < this.maxPhotos) {
        this.agendar(() => {
          this.iniciarCaptura();
        }, 500); // Pequeno delay para melhor UX
      }
    },

    fecharPopupLembrete() {
      this.showFinishReminderPopup = false;
    },

    async confirmarFotos() {
      if (this.photosTaken < this.maxPhotos) {
        this.$root.infoModal(
          "Você precisa tirar todas as fotos antes de continuar."
        );
        return;
      }

      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadStatus = "Salvando fotos localmente...";

      try {
        this.uploadProgress = 25;
        this.uploadStatus = "Enviando fotos para o servidor...";

        // 2) Enviar fotos para backend (Firestore/Storage) consumido pelo editor
        await this.salvarFotosNoBackend();
        this.uploadProgress = 90;
        this.uploadStatus = "Finalizando...";

        // 3) Atualizar etapa e navegar para o Editor.
        // Em caso de falha transitória de escrita da etapa, não bloquear a navegação.
        try {
          await setEtapaCompra(etapa.EDICAO);
        } catch (stageError) {
          console.warn("Falha ao atualizar etapa para EDICAO", stageError);
        }
        this.uploadProgress = 100;
        this.uploadStatus = "Concluído!";

        // Pequeno delay para mostrar o progresso completo
        await new Promise((resolve) => setTimeout(resolve, 500));

        posthog.capture("selfie_upload_completed", {
          photo_count: this.photosTaken,
        });
        this.$root.goto("/cabine/editor");
      } catch (error) {
        // Mensagem de erro mais específica
        let errorMessage = "Erro ao salvar as fotos. Tente novamente.";

        if (
          error.message.includes("timeout") ||
          error.message.includes("TIMEOUT") ||
          error.message.includes("Timeout no upload")
        ) {
          errorMessage =
            "Timeout no upload. Muitas fotos estão sendo processadas. Tente novamente.";
        } else if (
          error.message.includes("quota") ||
          error.message.includes("storage") ||
          error.message.includes("quota-exceeded")
        ) {
          errorMessage =
            "Erro de armazenamento. Tente novamente em alguns minutos.";
        } else if (
          error.message.includes("network") ||
          error.message.includes("NETWORK") ||
          error.message.includes("network-request-failed")
        ) {
          errorMessage =
            "Erro de conexão. Verifique sua internet e tente novamente.";
        } else if (error.message.includes("Falha ao salvar foto")) {
          errorMessage = error.message;
        } else if (
          error.message.includes("permission") ||
          error.message.includes("PERMISSION") ||
          error.message.includes("permission-denied")
        ) {
          errorMessage =
            "Erro de permissão. Verifique as configurações e tente novamente.";
        } else if (
          error.message.includes("unavailable") ||
          error.message.includes("UNAVAILABLE") ||
          error.message.includes("service-unavailable")
        ) {
          errorMessage =
            "Serviço temporariamente indisponível. Tente novamente em alguns minutos.";
        } else if (error.message.includes("Upload falhou após")) {
          errorMessage =
            "Falha no upload após várias tentativas. Verifique sua conexão e tente novamente.";
        }

        this.$root.infoModal(errorMessage);
      } finally {
        this.isUploading = false;
        this.uploadProgress = 0;
        this.uploadStatus = "";
      }
    },

    async salvarFotosNoBackend() {
      const compra = this.$store.getters["compra/getCompra"];
      const compraId = compra && compra.id;
      if (!compraId) throw new Error("Compra inválida para salvar fotos");

      const fotosCollection = collection(db, "fotos");
      const fotosValidas = this.gallery.filter((photo) => photo !== null);

      // Verificar se há muitas fotos e ajustar estratégia
      const BATCH_SIZE = fotosValidas.length > 6 ? 1 : 2; // 1 foto por vez se mais de 6 fotos
      const batches = [];

      for (let i = 0; i < fotosValidas.length; i += BATCH_SIZE) {
        batches.push(fotosValidas.slice(i, i + BATCH_SIZE));
      }

      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Atualizar status do progresso
        this.uploadStatus = `Enviando lote ${batchIndex + 1} de ${
          batches.length
        } (${fotosValidas.length} fotos total)...`;
        this.uploadProgress = 25 + (batchIndex / batches.length) * 65; // 25% a 90%

        // Processar fotos sequencialmente dentro do lote para evitar sobrecarga
        for (let localIdx = 0; localIdx < batch.length; localIdx++) {
          const photoDataUrl = batch[localIdx];
          const globalIdx = batchIndex * BATCH_SIZE + localIdx;
          const pathOriginal = `public/${compraId}/original/${
            globalIdx + 1
          }.jpg`;
          const pathEditada = `public/${compraId}/editada/${globalIdx + 1}.png`;

          // Atualizar status específico da foto
          this.uploadStatus = `Processando foto ${globalIdx + 1} de ${
            fotosValidas.length
          }...`;

          try {
            // Upload da imagem com retry
            await this.uploadWithRetry(photoDataUrl, pathOriginal, 3);

            await addDoc(fotosCollection, {
              compra_id: compraId,
              url_original: pathOriginal,
              url_editada: pathEditada,
            });

            // Pequena pausa entre fotos do mesmo lote
            if (localIdx < batch.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 500)); // Aumentei para 500ms
            }
          } catch (error) {
            // Tentar fallback: comprimir mais a foto e tentar novamente
            try {
              // const compressedPhoto = await this.optimizePhotoSize(
              //   photoDataUrl,
              //   200
              // );
              // Comprimir para 200KB
              await this.uploadWithRetry(photoDataUrl, pathOriginal, 2);

              // Adicionar documento no Firestore
              await addDoc(fotosCollection, {
                compra_id: compraId,
                url_original: pathOriginal,
                url_editada: pathEditada,
              });
            } catch (fallbackError) {
              throw new Error(
                `Falha ao salvar foto ${globalIdx + 1}: ${error.message}`
              );
            }
          }
        }

        // Pausa entre lotes para evitar sobrecarga
        if (batchIndex < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }
    },

    async uploadWithRetry(dataUrl, path, maxRetries = 3) {
      let lastError;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          // Adicionar timeout para upload
          const uploadPromise = uploadImageStorage(dataUrl, path);
          const timeoutPromise = new Promise(
            (_, reject) =>
              setTimeout(() => reject(new Error("Timeout no upload")), 30000) // 30 segundos
          );

          await Promise.race([uploadPromise, timeoutPromise]);
          return; // Sucesso, sair do loop
        } catch (error) {
          lastError = error;

          if (attempt < maxRetries) {
            // Aguardar antes da próxima tentativa (backoff exponencial)
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }

      throw new Error(
        `Upload falhou após ${maxRetries} tentativas: ${lastError.message}`
      );
    },

    cleanupCamera() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }

      // Cancela os timers agendados: sem isso, ao sair da tela no meio da
      // contagem eles ainda disparavam e reiniciavam a captura.
      this.timers.forEach((id) => clearTimeout(id));
      this.timers = [];

      this.isCountingDown = false;
      this.showLookAtCameraMessage = false;

      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      this.cameraReady = false;
    },

    showCaptureFeedback() {
      // Flash visual
      this.showFlash = true;
      this.agendar(() => {
        this.showFlash = false;
      }, 200);

      // Adicionar classe de feedback visual
      const video = this.$refs.video;
      if (video) {
        video.style.filter = "brightness(1.2) contrast(1.1)";
        this.agendar(() => {
          video.style.filter = "";
        }, 300);
      }

      // Tocar som de captura (opcional)
      this.playCaptureSound();
    },

    playCaptureSound() {
      // Beep simples via Web Audio API. Envolto em try/catch: o som é opcional
      // e NUNCA pode interromper o fluxo de captura. Reaproveita um único
      // AudioContext (ver `sharedAudioContext`) para não vazar contextos.
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;

        if (!sharedAudioContext) {
          sharedAudioContext = new AudioCtx();
        }
        const audioContext = sharedAudioContext;

        // O contexto pode iniciar suspenso (política de autoplay); retoma.
        if (audioContext.state === "suspended") {
          audioContext.resume().catch(() => {});
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.type = "sine";

        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContext.currentTime + 0.1
        );

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Falha ao tocar som de captura (ignorado):", e);
      }
    },

    voltar() {
      this.cleanupCamera();
      const totem = this.$store.getters["totem/getTotem"];
      setEtapaCompra(totem?.config?.selfie ? etapa.MODO_CAPTURA : etapa.QTDE_FOTO);
    },

    applyVideoOrientation() {
      if (this.video) {
        // Remover classes anteriores
        //this.video.classList.remove("landscape", "portrait");
        let w = this.getDefaultCamSize.width;
        let h = this.getDefaultCamSize.height;
        if (this.isPortraitMode) {
          this.camSize = { width: w, height: h };
        } else {
          this.camSize = { width: h, height: w };
        }
        //this.cleanupCamera();
        //this.initializeCamera();
      }
    },

    // Trocar de orientação reabre a câmera. Sem o await/trava, tocar rápido nos
    // dois botões disparava vários getUserMedia concorrentes: `this.stream` era
    // sobrescrito antes de o anterior ser parado, deixando MediaStreams órfãos
    // segurando a webcam — que então não abria mais sem reiniciar a máquina.
    async trocarOrientacao(retrato) {
      if (this.trocandoOrientacao) return;
      this.trocandoOrientacao = true;
      try {
        this.isPortraitMode = retrato;
        this.applyVideoOrientation();
        this.cleanupCamera();
        await this.initializeCamera();
      } finally {
        this.trocandoOrientacao = false;
      }
    },

    setPortraitMode() {
      this.trocarOrientacao(true);
    },

    setLandscapeMode() {
      this.trocarOrientacao(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.totemBG {
  background-size: cover;
  background-image: linear-gradient(
      180deg,
      #978fc4 0%,
      #a78ebd 34.84%,
      rgba(207, 138, 172, 0.8) 100%
    ),
    url("../../../assets/fundo.png");
}
.selfie-container {
  width: 100vw;
  height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  flex-direction: column;
  padding: 0px 20px;
  padding-top: 0px;
  position: relative;
}

.header-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 250px) minmax(192px, 1fr);
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.selfie-brand {
  grid-column: 2;
}

.selfie-navigation {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 16px;
}

.btn-voltar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
}

.logo {
  height: 50px;
  width: auto;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.orientation-buttons {
  display: flex;
  gap: 12px;
  width: 100%;
}

.orientation-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #8876a1 0%, #222129 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  min-height: 60px;
  box-shadow: 0px 8px 8px rgba(140, 38, 101, 0.4);

  &:hover {
    background: linear-gradient(180deg, #985d8f 0%, #1a191c 100%);
    transform: translateY(-2px);
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  &.active {
    background: linear-gradient(180deg, #985d8f 0%, #1a191c 100%);
    border-color: #8c2667;
    box-shadow: 0 6px 25px rgba(140, 38, 103, 0.4);
    font-weight: 700;

    &:hover {
      background: linear-gradient(180deg, #8876a1 0%, #222129 100%);
      box-shadow: 0 8px 30px rgba(140, 38, 103, 0.5);
    }
  }
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}

.camera-container {
  flex: 1; /* Ocupa todo o espaço disponível */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.camera-video {
  width: 100%;
  //width: 100%;
  height: 100%;
  object-fit: contain; // Sem zoom no modo paisagem
  //transform: scaleX(-1); // Espelhar para selfie
}

.camera-video.landscape {
  object-fit: contain; // Sem zoom no modo paisagem
}

.camera-video.portrait {
  object-fit: cover; // Cobrir tela no modo retrato
}

.camera-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: auto;
  cursor: pointer;
}

.capture-flash {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  opacity: 0;
  animation: flash 0.2s ease-out;
  z-index: 60;
}

@keyframes flash {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 0;
  }
}

.look-camera-message {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 55;
  animation: messageFadeIn 0.5s ease-out;
}

.message-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
}

.message-text {
  font-size: 4rem;
  font-weight: 900;
  color: white;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.8);
  animation: messagePulse 1s ease-in-out infinite;
  letter-spacing: 2px;
}

@keyframes messageFadeIn {
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes messagePulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.countdown-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  z-index: 50;
}

.countdown-center {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 300px;
  height: 300px;
}

.countdown-clock-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 8px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: clockRotate 1s linear infinite;
}

.countdown-clock-bg::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 4px;
  height: 120px;
  background: rgba(255, 255, 255, 0.8);
  transform: translate(-50%, -100%);
  transform-origin: bottom center;
  animation: clockHand 1s linear infinite;
}

.countdown-number {
  font-size: 12rem;
  font-weight: 900;
  color: white;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  animation: countdownPulse 1s ease-in-out;
  z-index: 1;
}

@keyframes countdownPulse {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes clockRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes clockHand {
  0% {
    transform: translate(-50%, -100%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -100%) rotate(360deg);
  }
}

.progress-indicator {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  padding: 12px 24px;
  backdrop-filter: blur(10px);
  z-index: 30;
}

.progress-text {
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 8px;
}

.progress-bar {
  width: 200px;
  height: 8px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #8c2667, #978fc4);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.instructions {
  position: absolute;
  bottom: 50%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.4);
  border-radius: 16px;
  padding: 16px 24px;
  backdrop-filter: blur(10px);
}

.instruction-text {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
}

.capture-button {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #8c2667, #978fc4);
  border: 4px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  z-index: 40;

  &:hover:not(:disabled) {
    transform: translateX(-50%) scale(1.1);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateX(-50%) scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.gallery-button {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.7);
  border: 3px solid white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  z-index: 40;
  position: relative;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
    transform: scale(1.1);
  }

  &.has-photos {
    background: linear-gradient(135deg, #8c2667, #978fc4);
    border-color: #8c2667;
  }
}

.photo-count {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  border: 2px solid white;
}

.mini-gallery {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 16px;
  padding: 12px;
  backdrop-filter: blur(10px);
  z-index: 30;
}

.mini-photo {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &.has-photo {
    border-color: #8c2667;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.mini-placeholder {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  font-weight: 600;
}

.upload-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.upload-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #8c2667;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.upload-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.upload-progress {
  width: 100%;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #8c2667 0%, #978fc4 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-percentage {
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
}

.next-photo-popup,
.finish-reminder-popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: popupFadeIn 0.3s ease-out;
}

.popup-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  background: white;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  max-width: 400px;
  text-align: center;
  animation: popupSlideIn 0.3s ease-out;
}

.popup-icon {
  color: #8c2667;
  animation: iconBounce 0.6s ease-out;
}

.popup-text h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.popup-text p {
  font-size: 1.125rem;
  color: #6b7280;
  margin: 8px 0 0 0;
  line-height: 1.5;
}

.popup-button {
  background: linear-gradient(135deg, #8c2667, #978fc4);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 32px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(140, 38, 103, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(140, 38, 103, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &.primary {
    background: linear-gradient(135deg, #8c2667, #978fc4);
  }

  &.secondary {
    background: linear-gradient(135deg, #6b7280, #4b5563);
    box-shadow: 0 4px 15px rgba(107, 114, 128, 0.3);

    &:hover {
      box-shadow: 0 6px 20px rgba(107, 114, 128, 0.4);
    }
  }
}

.popup-buttons {
  display: flex;
  gap: 12px;
  flex-direction: column;
  width: 100%;
}

@keyframes popupFadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes popupSlideIn {
  0% {
    transform: scale(0.8) translateY(20px);
    opacity: 0;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

@keyframes iconBounce {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(255, 255, 255, 0.1);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

/* Responsividade */
/* Monitor de 32 polegadas e telas grandes */
@media (min-width: 1920px) {
  .logo {
    height: 80px;
  }

  .btn-voltar {
    padding: 16px 28px;
    font-size: 1.25rem;
  }

  .orientation-buttons {
    padding: 24px;
    gap: 16px;
  }

  .orientation-btn {
    padding: 24px;
    font-size: 1.25rem;
    min-height: 70px;
  }

  .countdown-left,
  .countdown-right,
  .countdown-center {
    font-size: 12rem;
  }

  .message-text {
    font-size: 6rem;
  }

  .progress-text {
    font-size: 1.5rem;
  }

  .progress-bar {
    width: 300px;
    height: 12px;
  }

  .capture-button {
    width: 100px;
    height: 100px;
  }

  .gallery-button {
    width: 80px;
    height: 80px;
  }

  .mini-gallery {
    gap: 16px;
    padding: 16px;
  }

  .mini-photo {
    width: 80px;
    height: 80px;
  }

  .instruction-text {
    font-size: 2rem;
  }

  .popup-content {
    padding: 50px;
    max-width: 500px;
  }

  .popup-text h3 {
    font-size: 2rem;
  }

  .popup-text p {
    font-size: 1.25rem;
  }

  .popup-button {
    padding: 20px 40px;
    font-size: 1.25rem;
  }

  .popup-buttons {
    flex-direction: row;
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .logo {
    height: 40px;
  }

  .btn-voltar {
    padding: 8px 16px;
    font-size: 1rem;
  }

  .orientation-buttons {
    padding: 16px;
    gap: 8px;
  }

  .orientation-btn {
    padding: 16px;
    font-size: 0.9rem;
    min-height: 50px;
  }

  .countdown-left,
  .countdown-right,
  .countdown-center {
    font-size: 4rem;
  }

  .message-text {
    font-size: 2.5rem;
  }

  .countdown-left {
    left: 5%;
  }

  .countdown-right {
    right: 5%;
  }

  .capture-button {
    width: 70px;
    height: 70px;
  }

  .gallery-button {
    width: 50px;
    height: 50px;
  }

  .mini-gallery {
    gap: 8px;
    padding: 8px;
  }

  .mini-photo {
    width: 50px;
    height: 50px;
  }
}

@media (max-width: 480px) {
  .countdown-left,
  .countdown-right,
  .countdown-center {
    font-size: 3rem;
  }

  .message-text {
    font-size: 2rem;
  }

  .progress-bar {
    width: 150px;
  }

  .capture-button {
    width: 60px;
    height: 60px;
  }

  .mini-photo {
    width: 40px;
    height: 40px;
  }
}
</style>
